import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import { flattenPages, nextCursorParam } from '@/hooks/queries/pagination';
import { type FxSwapsApiFilters, type FxSwapsWireResponse, getFxSwapsPage } from '@/services/api/fx-swaps';

// Stable module-level select via flattenPages (pagination.ts) — the
// select-memoization rationale and the malformed-page `?? []` guard live there.
const selectAllFxSwaps = flattenPages((page: FxSwapsWireResponse) => page.records);

export const FX_SWAPS_QUERY_KEY = ['fx', 'swaps'] as const;

/**
 * Port of the legacy useFxSwaps accumulator: the apiFilters in the queryKey
 * reproduce the deep-diff reset (TanStack hashes the key structurally), and
 * `hasNextPage` from the cursor-only getNextPageParam reproduces the end guard —
 * once the cursor chain ends, scroll triggers can never restart from
 * page 1 and duplicate the list. Client-side filters never touch this hook.
 */
export const fxSwapsInfiniteQueryOptions = (apiFilters: FxSwapsApiFilters) =>
  infiniteQueryOptions({
    queryKey: [...FX_SWAPS_QUERY_KEY, apiFilters],
    queryFn: ({ pageParam }) => getFxSwapsPage(apiFilters, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursorParam,
    select: selectAllFxSwaps,
  });

export const useFxSwaps = (apiFilters: FxSwapsApiFilters) => useInfiniteQuery(fxSwapsInfiniteQueryOptions(apiFilters));
